import { useEffect, useMemo } from "react"
import dayjs from "dayjs"
import styled from "styled-components"

import { useLazyGetPerformanceTestsQuery } from "store/api/performanceTestApi"
import { useAppSelector } from "store/hooks"
import PerformanceTestStatus from "types/enums/PerformanceTestStatus"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"
import { ReactComponent as IconClock } from "assets/svg/clock.svg"

import hasSubscriptionAccess from "utils/hasSubscriptionAccess"

import { useCustomerDashboard } from "../CustomerDashboardContext"

const ScheduleCard = styled.div`
  border-radius: 8px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  align-items: center;
  padding: 12px 25px;
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
`

const TimeIconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  width: 26px;
  height: 26px;

  svg {
    width: 100%;
    height: 100%;

    path {
      stroke: #0f172a;
    }
  }
`

const ScheduleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ScheduleDateTime = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #0f172a;
`

const ScheduleDescription = styled.div`
  font-size: 15px;
  font-weight: 300;
  color: #0f172a;
`

const NoScheduleText = styled.div`
  font-size: 15px;
  font-weight: 400;
  color: #0f172a;
`

export const UpcomingScheduleCard = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const { devices, currentDevice } = useCustomerDashboard()
  const [triggerGetPerformanceTests, { data: performanceTestData }] = useLazyGetPerformanceTestsQuery()

  const hasPerformanceTestAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.PerformanceTest)
    : false

  const selectedDevice = devices[currentDevice]
  const deviceSN = selectedDevice?.sn

  useEffect(() => {
    if (deviceSN && hasPerformanceTestAccess) {
      triggerGetPerformanceTests({ sn: deviceSN })
    }
  }, [deviceSN, triggerGetPerformanceTests, hasPerformanceTestAccess])

  const device = useMemo(() => {
    return performanceTestData?.data?.[0] || null
  }, [performanceTestData])

  const scheduledPerformanceTest = useMemo(() => {
    if (!device?.perftest) return null

    const status = device.perftest.status?.toLowerCase()
    if (status !== PerformanceTestStatus.SCHEDULED && status !== PerformanceTestStatus.RESCHEDULED) {
      return null
    }

    return device.perftest
  }, [device])

  const getScheduledDateAndTime = () => {
    if (!scheduledPerformanceTest) return null

    if (scheduledPerformanceTest.schedule) {
      const dateTime = dayjs(scheduledPerformanceTest.schedule)
      return {
        date: dateTime.format("MMM D, YYYY"),
        time: dateTime.format("hh:mmA"),
      }
    }
    if (scheduledPerformanceTest.time) {
      const dateTime = dayjs(scheduledPerformanceTest.time)
      const date = dateTime.hour() < 12 ? dateTime : dateTime.add(1, "day")
      return {
        date: date.format("MMM D, YYYY"),
        time: "10:00AM - 12:00PM",
      }
    }

    return null
  }

  const getActionDescription = () => {
    if (!scheduledPerformanceTest) return null

    const action = scheduledPerformanceTest.action || "cooling"
    const actionText = action === "cooling" ? "Cooling" : "Heating"
    return `Turn on ${actionText} for 15 minutes`
  }

  const dateTime = getScheduledDateAndTime()
  const actionDescription = getActionDescription()

  const renderContent = () => {
    if (!scheduledPerformanceTest || !dateTime) {
      return <NoScheduleText>No Performance Test planned.</NoScheduleText>
    }

    return (
      <>
        <ScheduleDateTime>
          {dateTime.date} | {dateTime.time}
        </ScheduleDateTime>
        {actionDescription && <ScheduleDescription>{actionDescription}</ScheduleDescription>}
      </>
    )
  }

  return (
    <ScheduleCard>
      <TimeIconWrapper>
        <IconClock />
      </TimeIconWrapper>
      <ScheduleContent>{renderContent()}</ScheduleContent>
    </ScheduleCard>
  )
}
